import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import useCollectionsInfinite from '../../hooks/useCollectionsInfinite';
import useScrollEnter from '../../hooks/useScrollEnter';
import { usePageViewTracking } from '../../hooks/useTracking';
import { isMessage } from '../../lib/utils';

import { useBrand, useDefaultBrand } from '../../contexts/SiteContext';
import { typesSections } from '../../routes';
import HeaderAd from '../ads/HeaderAd';
import IndexDefaultHeader from '../headers/IndexDefaultHeader';
import InfiniteItemsBlocks from '../lists/InfiniteItemsBlocks';
import TypesMenu from '../menus/TypesMenu';

import styles from '../../styles/pages/collections-page.module.css';

// Types pour les props
interface CollectionsPageProps {
    brand?: string | null;
    type?: string | null;
    sponsorised?: boolean;
    current?: boolean;
    className?: string | null;
    contentClassName?: string | null;
    onEnter?: (() => void) | null;
    onLeave?: (() => void) | null;
}

function CollectionsPage({
    brand: brandSlug = null,
    type: typeSlug = null,
    sponsorised = false,
    current = false,
    className = null,
    contentClassName = null,
    onEnter = null,
    onLeave = null,
}: CollectionsPageProps) {
    const intl = useIntl();
    const type = typesSections.find(({ id, path }) => id === typeSlug || path === typeSlug);
    const { id: typeId, title, query: typeQuery } = type || {};
    const hasBrand = brandSlug !== null;
    const { handle: defaultBrandHandle = 'urbania' } = useDefaultBrand();
    const brand = useBrand(brandSlug, !hasBrand ? defaultBrandHandle : null);
    const { handle: brandHandle = null } = brand || {};

    useScrollEnter({
        disabled: !current,
        onEnter: onEnter || undefined,
        onLeave: onLeave || undefined,
    });

    usePageViewTracking(current, {
        brand,
        pageType: 'index',
    });

    const isSeries = typeId === 'series' || typeId === 'videos' || typeId === 'podcasts';

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.isSeries]: isSeries,
                },
                className,
                contentClassName,
            ])}
            data-brand={brandHandle}
            // data-theme={isSeries && !sponsorised ? 'videos' : null}
        >
            <HeaderAd />
            <IndexDefaultHeader
                title={
                    <>
                        {isMessage(title) ? intl.formatMessage(title) : title}
                        {sponsorised ? (
                            <small>
                                <FormattedMessage
                                    defaultMessage="Sponso"
                                    description="Page title"
                                />
                            </small>
                        ) : null}
                    </>
                }
                className={styles.header}
            />
            <TypesMenu brand={brand} sponsorised={sponsorised} className={styles.filters} />
            <main className={styles.content}>
                <InfiniteItemsBlocks
                    query={{
                        order: 'new',
                        brand: brandHandle !== 'urbania' ? brand.id : null,
                        ...typeQuery,
                        placement: [
                            `${typeId === 'series' ? 'videos' : typeId}_collections_${brandHandle}`,
                        ],
                        ...(sponsorised ? { sponsorised: true } : null),
                    }}
                    itemsBlocks={[
                        isSeries
                            ? {
                                  type: 'items',
                                  itemsType: 'collections',
                                  presentation: 'grid',
                                  cardWidth: 150,
                                  cardSize: 'medium',
                                  cardTheme: 'rounded',
                                  cardRatio: 'vertical',
                                  minColumns: 2,
                                  count: 24,
                              }
                            : {
                                  type: 'items',
                                  itemsType: 'collections',
                                  presentation: 'grid',
                                  cardWidth: 350,
                                  cardSize: 'big',
                                  cardTheme: 'rounded',
                                  cardRatio: 'horizontal',
                                  count: 18,
                                  maxColumns: 4,
                              },
                    ]}
                    hook="collections"
                    itemsType="collections"
                    cardType="vertical"
                    cardWithout={['type-icon']}
                    cardWith={!isSeries ? ['format'] : []}
                />
            </main>
        </div>
    );
}

export default CollectionsPage;
